import React, { ReactNode, useMemo, useRef } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { ITicketBoard } from '../../../../../interfaces/models/ticket.interfaces'
import {
  infiniteTicketQueries,
  sendTicketMessageAsBoard,
} from '../../../../../api/ticket'
import { InfiniteScroll } from '../../../../../components/common/InfiniteScroll/InfiniteScroll'
import { TicketMessage } from './TicketMessage/TicketMessage'
import { theme } from '../../../../../theme/theme'
import { TicketAvatar } from '../../../../../components/common/Avatar'
import { Icon, IconChoices } from '../../../../../components/common/Icon'
import { TicketChatBar } from '../../../../../components/ticket/TicketChatBar'
import { TicketBadge } from '../../../../../components/ticket/TicketBadge'
import { TicketClosedBanner } from '../../../../../components/ticket/TicketClosedBanner'

type TicketChatProps = {
  ticket: ITicketBoard
  showSideBarButton?: boolean
  onOpenSideBar?: () => void
}

const TicketChatBarHandler = ({
  societyId,
  ticket,
}: {
  societyId: string
  ticket: ITicketBoard
}): ReactNode => {
  const queryClient = useQueryClient()
  const { mutate: mutateSendMessage, isPending: isSendingMessage } =
    useMutation({
      mutationFn: async (variables: {
        message?: string
        documentIds?: string[]
        mediaIds?: string[]
      }) => {
        const response = await sendTicketMessageAsBoard(
          { societyId, ticketId: ticket.id },
          {
            documents: variables.documentIds?.map((id) => ({ id })),
            media: variables.mediaIds?.map((id) => ({ id })),
            message: variables.message,
          }
        )

        const data = response?.data?.data

        if (!data) {
          throw new Error('No data response')
        }

        return data
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: infiniteTicketQueries.ticketsBoard({ societyId }).queryKey,
        })
      },
    })

  return (
    <TicketChatBar
      societyId={societyId}
      isSendingMessage={isSendingMessage}
      mutateSendMessage={mutateSendMessage}
    />
  )
}

const TicketChatBody = ({
  societyId,
  ticketId,
}: {
  societyId: string
  ticketId: string
}): ReactNode => {
  const hideContent = false
  const ref = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const {
    data: queryMessages,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    infiniteTicketQueries.getTicketMessagesAsBoard({ societyId, ticketId })
  )
  const messages = useMemo(() => {
    return queryMessages?.pages.flatMap((page) => page.items || [])
  }, [queryMessages])

  const onEndReached = (): void => {
    if (!isFetching && hasNextPage) {
      fetchNextPage()
    }
  }
  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <div ref={ref} className="flex h-full bg-neutral-98">
          <InfiniteScroll
            key="infiniteChatRoomScroll" // Add key to prompt rerendering of component on state change
            className={`flex flex-col-reverse items-start bg-neutral-98 pl-10 pr-4 pt-8 opacity-0
              md:pl-12 md:pr-6
              ${hideContent ? 'opacity-0' : 'opacity-100'}
            `}
            overflow="overflow-y-scroll"
            onEndReached={onEndReached}
            rootRef={ref}
          >
            <div ref={bottomRef} />
            {messages?.map((_message, index) => {
              const previousMessage =
                index === 0 ? undefined : messages[index - 1]

              return (
                <TicketMessage
                  key={_message.id}
                  ticketMessage={_message}
                  previousMessage={previousMessage}
                  nextMessage={
                    index === messages.length - 1
                      ? undefined
                      : messages[index + 1]
                  }
                  firstMessageInChat={index === 0}
                />
              )
            })}
          </InfiniteScroll>
        </div>
      </div>
    </>
  )
}
const TicketChatHeader = ({
  ticket,
  showSideBarButton,
  onOpenSideBar,
}: TicketChatProps): ReactNode => {
  return (
    <div className="bg-white px-8 py-4 flex justify-between w-full">
      <div className="flex gap-2 items-center">
        {ticket.customer && <TicketAvatar user={ticket.customer} />}
        <div>
          <TicketBadge status={ticket.status} />
          <p
            style={theme.textVariants.baseBold}
            className="pl-1.5 line-clamp-1 mt-1"
          >
            {ticket.title}
          </p>
          <span
            style={theme.textVariants.caption}
            className="block pl-1.5 whitespace-nowrap text-neutral-50"
          >
            #{ticket.ticketNumber}
          </span>
        </div>
      </div>
      {showSideBarButton && (
        <button
          type="button"
          onClick={onOpenSideBar}
          className="m-0 rounded border-0 bg-transparent p-0"
        >
          <Icon icon={IconChoices.MORE} />
        </button>
      )}
    </div>
  )
}

export const TicketChatBoard = ({
  ticket,
  showSideBarButton,
  onOpenSideBar,
}: TicketChatProps): ReactNode => {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <TicketChatHeader
        ticket={ticket}
        showSideBarButton={showSideBarButton}
        onOpenSideBar={onOpenSideBar}
      />
      <TicketChatBody societyId={ticket.societyId} ticketId={ticket.id} />
      {ticket.status === 'closed' ? (
        <TicketClosedBanner />
      ) : (
        <TicketChatBarHandler societyId={ticket.societyId} ticket={ticket} />
      )}
    </div>
  )
}
